import { useCallback, useRef } from "react";
import { ImageIcon, PlusIcon, XIcon } from "lucide-react";

import {
  FieldDefinitionFieldType,
  FieldDefinitionPicturesType,
} from "@/components/@types";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";

type PictureValue = {
  id: string;
  identifier: string;
  name?: string | null;
  url?: string | null;
  size?: string | null;
  /** When the picture was just selected locally (not yet uploaded). */
  _file?: File;
};

function PictureCard({
  picture,
  isEditable,
  onRemove,
}: {
  picture: PictureValue;
  isEditable: boolean;
  onRemove: () => void;
}) {
  return (
    // Under the pointer, its remove button shown. (Its edge was to light in
    // primary's 400 too, read from the theme in code: in this theme that
    // step is a channel, not a colour, so it never drew, and does not.)
    <div className="relative w-16 h-16 rounded-sm overflow-hidden [border:1px_solid_var(--border)] shrink-0 [transition:border-color_0.15s] [&:hover_.picture-remove-btn]:opacity-100">
      {picture.url ? (
        <img
          src={picture.url}
          alt={picture.name || "Picture"}
          className="w-full h-full object-cover block"
        />
      ) : (
        <div className="flex items-center justify-center w-full h-full bg-accent">
          <ImageIcon
            size={20}
            color={
              "color-mix(in oklab, var(--muted-foreground) 80%, transparent)"
            }
          />
        </div>
      )}

      {isEditable && (
        <Button
          variant="destructive"
          size="icon-xs"
          className="picture-remove-btn absolute top-0.5 right-0.5 opacity-[0] [transition:opacity_0.15s] rounded-full"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <XIcon size={14} />
        </Button>
      )}
    </div>
  );
}

function AddPictureCard({ onClick }: { onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      className="w-16 h-16 rounded-sm [border-width:1px] [border-style:dashed] border-border flex items-center justify-center flex-col gap-0.5 cursor-pointer shrink-0 [transition:border-color_0.15s,background-color_0.15s] bg-muted hover:border-ring hover:bg-accent"
    >
      <PlusIcon size={20} color={"var(--muted-foreground)"} />
      <Typography
        level="body-xs"
        className="text-muted-foreground/80 text-[9px] [user-select:none]"
      >
        Add
      </Typography>
    </div>
  );
}

export function FieldPictures({
  value,
  events,
  size = "md",
  style,
}: FieldDefinitionFieldType<FieldDefinitionPicturesType>) {
  const pictures: PictureValue[] = value || [];
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isEditable = !!events?.onChangeField;

  const handleAddClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFilesSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      const newPictures: PictureValue[] = Array.from(files).map((file) => {
        const tempId = `temp-${crypto.randomUUID()}`;
        return {
          id: tempId,
          identifier: tempId,
          name: file.name,
          url: URL.createObjectURL(file),
          size: String(file.size),
          _file: file,
        };
      });

      events?.onChangeField?.([...pictures, ...newPictures]);

      // Reset the input so the same file(s) can be re-selected
      e.target.value = "";
    },
    [events, pictures],
  );

  const handleRemove = useCallback(
    (index: number) => {
      const removed = pictures[index];
      // Revoke the object URL to free memory if it was a local preview
      if (removed?._file && removed.url) {
        URL.revokeObjectURL(removed.url);
      }
      const next = pictures.filter((_, i) => i !== index);
      events?.onChangeField?.(next);
    },
    [events, pictures],
  );

  if (pictures.length === 0 && !isEditable) {
    return (
      <div
        className="flex items-center gap-2 text-muted-foreground/80 pt-1 pb-1"
        style={style}
      >
        <ImageIcon size={size === "sm" ? 12 : 14} />
        <Typography level="body-sm" className="text-muted-foreground/80">
          No pictures
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-row flex-wrap gap-2" style={style}>
      {pictures.map((picture, index) => (
        <PictureCard
          key={picture.id}
          picture={picture}
          isEditable={isEditable}
          onRemove={() => handleRemove(index)}
        />
      ))}

      {isEditable && <AddPictureCard onClick={handleAddClick} />}

      {/* Hidden file input – accepts images, multi-select enabled */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={handleFilesSelected}
      />
    </div>
  );
}
